/**
 * Whether the "Respond" composer should be rendered.
 *
 * ChatContainerReader is shared between the READER conversation and the
 * standalone assistants (the Altar affirmation/name/altar helper, the Voice).
 * Its lock came from `lock_interface`, a module-level ref in
 * useReaderConversation set from the reader conversation's `lock_chat`.
 *
 * The Doorman sets `lock_chat = True` — it is a buttons-only state, correctly
 * so. But because the ref is global, a seeker sitting in the Doorman also lost
 * the composer inside the Altar affirmation assistant, which is a different
 * conversation with nothing to lock. The mode bar still rendered above it, so
 * the sheet looked functional and simply could not be typed into.
 *
 * `standalone` marks the assistants: they own their own conversation and never
 * inherit the reader's lock or its card-pick state.
 */
export function showComposer({
  standalone = false,
  chatLocked = false,
  pickingCards = false,
  readingActive = false,
  chatAboutOpen = false,
  hasActions = true,
  deckOnScreen = true,
} = {}) {
  if (standalone) return true

  // LAST-RESORT GUARD. A locked chat is legitimate — states lock it and offer
  // buttons instead, and the buttons are the way out. But if those actions are
  // not there, the seeker has no composer and nothing to press: a dead end
  // with no way forward at all. This happened twice in production, by two
  // different routes, so the app should be structurally incapable of
  // rendering it rather than merely unlikely to.
  //
  // Unlocking is the recovery, not refetching: it always leaves a way to
  // speak, and it cannot fail the same way a second time.
  // The deck on screen is the way forward (lesson walk B4): a locked pick
  // with no chips is not a dead end, and a Respond box under it is noise.
  if (pickingCards && deckOnScreen) return false
  if (chatLocked && !hasActions) return true
  if (pickingCards && !deckOnScreen && !hasActions) return true

  if (chatLocked || pickingCards) return false
  return !readingActive || chatAboutOpen
}
